/**
 * Compiles a whole policy draft (plan §5.1).
 *
 * The compiler is the gate `policies.validateDraft` and `policies.publish` stand behind, and it is also
 * what the browser runs on every keystroke, so it answers with a **list** of problems rather than the
 * first one: an admin fixing four equations should see four messages, not four round trips.
 *
 * It still returns a usable `policy` whenever it can. An equation that will not parse, that names
 * something nobody supplies or that sits in a cycle is marked broken and its dependents fail with it,
 * while every sound equation continues to produce a preview figure. Publishing is refused by the
 * service when `ok` is false; previewing is not.
 */
import { asEngineError, expressionError } from '../errors.js';
import { CUSTOM_ID_PATTERN, isSystemEquationId, SYSTEM_EQUATION_IDS } from '../seed-policy-v1.js';
import { isUnitName } from '../types.js';
import { findInput, INPUT_CATALOG, isTradeInInput } from '../valuation/input-catalog.js';
import { checkDeclaredUnit } from '../units/infer.js';
import { parseExpression } from './parser.js';
const unitOrFallback = (unit) => (isUnitName(unit) ? unit : 'JOD');
export const compilePolicy = (equations, options = {}) => {
    const catalog = options.catalog ?? INPUT_CATALOG;
    const requireSystemOutputs = options.requireSystemOutputs ?? true;
    const catalogNames = new Set(catalog.map((entry) => entry.name));
    const lookupInput = catalog === INPUT_CATALOG ? findInput : (name) => catalog.find((entry) => entry.name === name);
    const tradeInInput = catalog === INPUT_CATALOG ? isTradeInInput : (name) => lookupInput(name)?.source === 'TRADE_IN';
    const errors = [];
    const compiled = [];
    const byId = {};
    // ── ids ───────────────────────────────────────────────────────────────────────────────────────
    for (const def of equations) {
        if (byId[def.id]) {
            errors.push(expressionError('RESERVED_ID', `"${def.id}" is defined more than once`, { equationId: def.id, token: def.id }));
            continue;
        }
        if (def.kind === 'SYSTEM' && !isSystemEquationId(def.id)) {
            errors.push(expressionError('RESERVED_ID', `"${def.id}" is not one of the system equations and cannot be marked SYSTEM`, {
                equationId: def.id,
                token: def.id,
            }));
        }
        if (def.kind === 'CUSTOM') {
            if (isSystemEquationId(def.id)) {
                errors.push(expressionError('RESERVED_ID', `"${def.id}" is a system equation id and cannot be reused for a custom equation`, {
                    equationId: def.id,
                    token: def.id,
                }));
            }
            else if (!CUSTOM_ID_PATTERN.test(def.id)) {
                errors.push(expressionError('RESERVED_ID', `"${def.id}" is not a valid custom id — use x_ followed by a name, such as x_marketAdjust`, {
                    equationId: def.id,
                    token: def.id,
                }));
            }
        }
        if (catalogNames.has(def.id)) {
            errors.push(expressionError('RESERVED_ID', `"${def.id}" is the name of an input and cannot also be an equation`, {
                equationId: def.id,
                token: def.id,
            }));
        }
        if (!isUnitName(def.unit)) {
            errors.push(expressionError('UNIT_MISMATCH', `"${def.unit}" is not a supported unit`, { equationId: def.id, token: String(def.unit) }));
        }
        const entry = { def, parsed: null, dependsOn: [], inputs: [] };
        compiled.push(entry);
        byId[def.id] = entry;
    }
    // ── parse ─────────────────────────────────────────────────────────────────────────────────────
    for (const entry of compiled) {
        try {
            entry.parsed = parseExpression(entry.def.expression);
        }
        catch (error) {
            const parsed = { ...asEngineError(error), equationId: entry.def.id };
            entry.error = parsed;
            errors.push(parsed);
        }
    }
    // ── references and scope ──────────────────────────────────────────────────────────────────────
    const referencedInputs = new Set();
    for (const entry of compiled) {
        if (!entry.parsed) {
            continue;
        }
        for (const reference of entry.parsed.references) {
            if (byId[reference]) {
                entry.dependsOn.push(reference);
                if (entry.def.scope === 'ALWAYS' && byId[reference].def.scope === 'TRADE_IN') {
                    errors.push(expressionError('SCOPE_LEAK', `"${entry.def.id}" always runs but reads "${reference}", which only exists for a trade-in`, {
                        equationId: entry.def.id,
                        token: reference,
                    }));
                }
                continue;
            }
            entry.inputs.push(reference);
            if (!catalogNames.has(reference)) {
                errors.push(expressionError('UNKNOWN_REFERENCE', `"${reference}" is neither an equation nor an available input`, {
                    equationId: entry.def.id,
                    token: reference,
                }));
                continue;
            }
            referencedInputs.add(reference);
            if (entry.def.scope === 'ALWAYS' && tradeInInput(reference)) {
                errors.push(expressionError('SCOPE_LEAK', `"${entry.def.id}" always runs but reads the trade-in input "${reference}"`, {
                    equationId: entry.def.id,
                    token: reference,
                }));
            }
        }
    }
    // ── cycles and evaluation order ───────────────────────────────────────────────────────────────
    const order = [];
    const state = new Map();
    const broken = new Set(compiled.filter((entry) => entry.error).map((entry) => entry.def.id));
    const path = [];
    const visit = (id) => {
        if (state.get(id) === 'done' || broken.has(id)) {
            return;
        }
        if (state.get(id) === 'visiting') {
            const cycle = [...path.slice(path.indexOf(id)), id].join(' → ');
            for (const member of path.slice(path.indexOf(id))) {
                if (!broken.has(member)) {
                    broken.add(member);
                    const error = expressionError('CIRCULAR', `"${member}" depends on itself through ${cycle}`, { equationId: member, token: id });
                    byId[member].error = error;
                    errors.push(error);
                }
            }
            return;
        }
        state.set(id, 'visiting');
        path.push(id);
        for (const dependency of byId[id].dependsOn) {
            visit(dependency);
        }
        path.pop();
        state.set(id, 'done');
        if (!broken.has(id)) {
            order.push(id);
        }
    };
    for (const entry of compiled) {
        visit(entry.def.id);
    }
    // ── units ─────────────────────────────────────────────────────────────────────────────────────
    const resolveUnit = (name) => {
        const equation = byId[name];
        if (equation) {
            return unitOrFallback(equation.def.unit);
        }
        const input = lookupInput(name);
        return input ? input.unit : null;
    };
    for (const entry of compiled) {
        if (!entry.parsed || broken.has(entry.def.id)) {
            continue;
        }
        try {
            checkDeclaredUnit(entry.parsed.ast, unitOrFallback(entry.def.unit), resolveUnit, entry.def.id);
        }
        catch (error) {
            errors.push({ ...asEngineError(error), equationId: entry.def.id });
        }
    }
    // ── required system outputs ───────────────────────────────────────────────────────────────────
    if (requireSystemOutputs) {
        for (const id of SYSTEM_EQUATION_IDS) {
            if (!byId[id]) {
                errors.push(expressionError('MISSING_SYSTEM_OUTPUT', `The system equation "${id}" is missing from this policy`, { token: id }));
            }
        }
    }
    return {
        ok: errors.length === 0,
        errors,
        policy: {
            equations: compiled,
            byId,
            order,
            referencedInputs: [...referencedInputs],
            catalog,
        },
    };
};
//# sourceMappingURL=policy-compiler.js.map